import { useState, useEffect, useCallback } from "react";
import * as api from "../api/client.js";
import type { Connector, BuiltinTool, MCPServer, MCPTool } from "../api/client.js";

// ── Types ──────────────────────────────────────────────────────────────────

type TabView = "builtin" | "connectors" | "mcp";

const RISK_COLORS: Record<string, string> = {
  low: "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const LIFECYCLE_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  published: "bg-green-100 text-green-700",
  deprecated: "bg-red-100 text-red-600",
};

// ── Main Component ──────────────────────────────────────────────────────────

export function ToolMarketplace() {
  const [tab, setTab] = useState<TabView>("builtin");
  const [builtinTools, setBuiltinTools] = useState<BuiltinTool[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showRegisterConnector, setShowRegisterConnector] = useState(false);
  const [showRegisterMcp, setShowRegisterMcp] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [selectedMcp, setSelectedMcp] = useState<MCPServer | null>(null);
  const [mcpTools, setMcpTools] = useState<MCPTool[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [connRes, mcpRes] = await Promise.all([
        api.listConnectors(),
        api.listMcpServers(),
      ]);
      setBuiltinTools(connRes.builtinTools);
      setConnectors(connRes.connectors);
      setMcpServers(mcpRes.servers);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tools");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDiscoverTools = async (serverId: string) => {
    try {
      const result = await api.discoverMcpTools(serverId);
      setMcpTools(result.tools);
    } catch {
      setMcpTools([]);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
        <div>
          <h1 className="text-xl font-bold text-ink-950">Tool Marketplace</h1>
          <p className="text-sm text-ink-500">
            Built-in tools, custom connectors, and MCP server integrations
          </p>
        </div>
        <div className="flex gap-2">
          {tab === "connectors" && (
            <button
              onClick={() => setShowRegisterConnector(true)}
              className="rounded-lg bg-ink-950 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800"
            >
              Register Connector
            </button>
          )}
          {tab === "mcp" && (
            <button
              onClick={() => setShowRegisterMcp(true)}
              className="rounded-lg bg-ink-950 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800"
            >
              Add MCP Server
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-ink-100 px-6">
        {(
          [
            { key: "builtin", label: "Built-in Tools", count: builtinTools.length },
            { key: "connectors", label: "Connectors", count: connectors.length },
            { key: "mcp", label: "MCP Servers", count: mcpServers.length },
          ] as const
        ).map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              tab === key
                ? "border-b-2 border-ink-950 text-ink-950"
                : "text-ink-500 hover:text-ink-700"
            }`}
          >
            {label}
            <span className="ml-1.5 rounded-full bg-surface-2 px-1.5 py-0.5 text-xs">
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-ink-400">
            Loading tools...
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && tab === "builtin" && (
          <BuiltinToolsGrid tools={builtinTools} />
        )}
        {!loading && !error && tab === "connectors" && (
          <ConnectorsGrid
            connectors={connectors}
            onSelect={setSelectedConnector}
            onRefresh={loadData}
          />
        )}
        {!loading && !error && tab === "mcp" && (
          <MCPServersGrid
            servers={mcpServers}
            onSelect={(s) => {
              setSelectedMcp(s);
              handleDiscoverTools(s.id);
            }}
            onRefresh={loadData}
          />
        )}
      </div>

      {/* Connector Detail Panel */}
      {selectedConnector && (
        <DetailPanel
          title={selectedConnector.name}
          onClose={() => setSelectedConnector(null)}
        >
          <div className="space-y-4">
            <div className="flex gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LIFECYCLE_COLORS[selectedConnector.lifecycle]}`}>
                {selectedConnector.lifecycle}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_COLORS[selectedConnector.riskLevel]}`}>
                {selectedConnector.riskLevel} risk
              </span>
            </div>
            {selectedConnector.description && (
              <p className="text-sm text-ink-600">{selectedConnector.description}</p>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase text-ink-400">Slug</h4>
              <p className="mt-1 font-mono text-sm text-ink-700">{selectedConnector.slug}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase text-ink-400">Version</h4>
              <p className="mt-1 text-sm text-ink-700">{selectedConnector.version}</p>
            </div>
            {selectedConnector.endpoint && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-ink-400">Endpoint</h4>
                <p className="mt-1 break-all font-mono text-xs text-ink-600">{selectedConnector.endpoint}</p>
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase text-ink-400">Execution Mode</h4>
              <p className="mt-1 text-sm text-ink-700">{selectedConnector.executionMode}</p>
            </div>
            {selectedConnector.operations.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-ink-400">
                  Operations ({selectedConnector.operations.length})
                </h4>
                <div className="mt-2 space-y-2">
                  {selectedConnector.operations.map((op) => (
                    <div key={op.name} className="rounded-lg border border-ink-100 p-3">
                      <p className="text-sm font-medium text-ink-800">{op.name}</p>
                      <p className="mt-0.5 text-xs text-ink-500">{op.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DetailPanel>
      )}

      {/* MCP Server Detail Panel */}
      {selectedMcp && (
        <DetailPanel
          title={selectedMcp.name}
          onClose={() => { setSelectedMcp(null); setMcpTools([]); }}
        >
          <div className="space-y-4">
            {selectedMcp.description && (
              <p className="text-sm text-ink-600">{selectedMcp.description}</p>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase text-ink-400">Server URL</h4>
              <p className="mt-1 break-all font-mono text-xs text-ink-600">{selectedMcp.url}</p>
            </div>
            {selectedMcp.allowlist && selectedMcp.allowlist.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-ink-400">Allowlist</h4>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedMcp.allowlist.map((t) => (
                    <span key={t} className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {selectedMcp.blocklist && selectedMcp.blocklist.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-ink-400">Blocklist</h4>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedMcp.blocklist.map((t) => (
                    <span key={t} className="rounded bg-red-50 px-2 py-0.5 text-xs text-red-700">{t}</span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase text-ink-400">
                Discovered Tools ({mcpTools.length})
              </h4>
              {mcpTools.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {mcpTools.map((t) => (
                    <div key={t.name} className="rounded-lg border border-ink-100 p-3">
                      <p className="text-sm font-medium text-ink-800">{t.name}</p>
                      <p className="mt-0.5 text-xs text-ink-500">{t.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-ink-400">
                  No tools discovered. The server may be unreachable.
                </p>
              )}
            </div>
          </div>
        </DetailPanel>
      )}

      {/* Register Connector Modal */}
      {showRegisterConnector && (
        <RegisterConnectorModal
          onClose={() => setShowRegisterConnector(false)}
          onCreated={() => { setShowRegisterConnector(false); loadData(); }}
        />
      )}

      {/* Register MCP Server Modal */}
      {showRegisterMcp && (
        <RegisterMcpModal
          onClose={() => setShowRegisterMcp(false)}
          onCreated={() => { setShowRegisterMcp(false); loadData(); }}
        />
      )}
    </div>
  );
}

// ── Built-in Tools Grid ──────────────────────────────────────────────────────

function BuiltinToolsGrid({ tools }: { tools: BuiltinTool[] }) {
  if (tools.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-400">
        No built-in tools available
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {tools.map((tool) => (
        <div key={tool.id} className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold text-ink-900">{tool.name}</h3>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_COLORS[tool.riskLevel]}`}>
              {tool.riskLevel}
            </span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-ink-500">{tool.description}</p>
          <div className="mt-3 flex items-center gap-2">
            <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
              built-in
            </span>
            {tool.enabled && (
              <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-600">
                enabled
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Connectors Grid ──────────────────────────────────────────────────────────

function ConnectorsGrid({
  connectors,
  onSelect,
  onRefresh,
}: {
  connectors: Connector[];
  onSelect: (c: Connector) => void;
  onRefresh: () => void;
}) {
  const handlePublish = async (id: string) => {
    await api.publishConnector(id);
    onRefresh();
  };
  const handleDelete = async (id: string) => {
    await api.deleteConnector(id);
    onRefresh();
  };

  if (connectors.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-ink-200 p-8 text-center">
        <p className="text-sm text-ink-500">No custom connectors registered</p>
        <p className="mt-1 text-xs text-ink-400">
          Register a connector to extend the tool ecosystem
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {connectors.map((c) => (
        <div
          key={c.id}
          className="cursor-pointer rounded-xl border border-ink-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          onClick={() => onSelect(c)}
        >
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold text-ink-900">{c.name}</h3>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LIFECYCLE_COLORS[c.lifecycle]}`}>
              {c.lifecycle}
            </span>
          </div>
          <p className="mt-1 font-mono text-xs text-ink-400">{c.slug}</p>
          {c.description && (
            <p className="mt-2 line-clamp-2 text-xs text-ink-500">{c.description}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_COLORS[c.riskLevel]}`}>
              {c.riskLevel}
            </span>
            <span className="rounded bg-surface-2 px-2 py-0.5 text-xs text-ink-600">
              {c.operations.length} ops
            </span>
            <span className="rounded bg-surface-2 px-2 py-0.5 text-xs text-ink-600">
              {c.executionMode}
            </span>
          </div>
          <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
            {c.lifecycle === "draft" && (
              <button
                onClick={() => handlePublish(c.id)}
                className="rounded px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50"
              >
                Publish
              </button>
            )}
            <button
              onClick={() => handleDelete(c.id)}
              className="rounded px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MCP Servers Grid ────────────────────────────────────────────────────────

function MCPServersGrid({
  servers,
  onSelect,
  onRefresh,
}: {
  servers: MCPServer[];
  onSelect: (s: MCPServer) => void;
  onRefresh: () => void;
}) {
  const handleDelete = async (id: string) => {
    await api.deleteMcpServer(id);
    onRefresh();
  };

  if (servers.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-ink-200 p-8 text-center">
        <p className="text-sm text-ink-500">No MCP servers registered</p>
        <p className="mt-1 text-xs text-ink-400">
          Add an MCP server to integrate external tools
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {servers.map((s) => (
        <div
          key={s.id}
          className="cursor-pointer rounded-xl border border-ink-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          onClick={() => onSelect(s)}
        >
          <h3 className="text-sm font-semibold text-ink-900">{s.name}</h3>
          {s.description && (
            <p className="mt-1 line-clamp-2 text-xs text-ink-500">{s.description}</p>
          )}
          <p className="mt-2 break-all font-mono text-xs text-ink-400">{s.url}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {s.allowlist && s.allowlist.length > 0 && (
              <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-600">
                {s.allowlist.length} allowed
              </span>
            )}
            {s.blocklist && s.blocklist.length > 0 && (
              <span className="rounded bg-red-50 px-2 py-0.5 text-xs text-red-600">
                {s.blocklist.length} blocked
              </span>
            )}
            {s.discoveredTools && s.discoveredTools.length > 0 && (
              <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-600">
                {s.discoveredTools.length} tools
              </span>
            )}
          </div>
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => handleDelete(s.id)}
              className="rounded px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50"
            >
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Detail Panel ────────────────────────────────────────────────────────────

function DetailPanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-y-0 right-0 z-50 flex w-96 flex-col border-l border-ink-100 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-ink-100 px-5 py-4">
        <h2 className="text-base font-semibold text-ink-900">{title}</h2>
        <button
          onClick={onClose}
          className="rounded p-1 text-ink-400 hover:bg-surface-2 hover:text-ink-700"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-auto p-5">{children}</div>
    </div>
  );
}

// ── Register Connector Modal ────────────────────────────────────────────────

function RegisterConnectorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [riskLevel, setRiskLevel] = useState("medium");
  const [executionMode, setExecutionMode] = useState<"sandbox" | "native">("sandbox");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!slug || !name) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.createConnector({
        slug,
        name,
        description: description || undefined,
        endpoint: endpoint || undefined,
        riskLevel,
        executionMode,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to register connector");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-ink-900">Register Connector</h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-600">Slug (unique ID)</label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              placeholder="my-connector"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Custom Connector"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">Endpoint URL</label>
            <input
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="https://my-connector.example.com/api"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-ink-600">Risk Level</label>
              <select
                value={riskLevel}
                onChange={(e) => setRiskLevel(e.target.value)}
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-ink-600">Execution Mode</label>
              <select
                value={executionMode}
                onChange={(e) => setExecutionMode(e.target.value as "sandbox" | "native")}
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
              >
                <option value="sandbox">Sandbox (secure)</option>
                <option value="native">Native (trusted)</option>
              </select>
            </div>
          </div>
          {error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm text-ink-600 hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!slug || !name || submitting}
            className="rounded-lg bg-ink-950 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800 disabled:opacity-50"
          >
            {submitting ? "Registering..." : "Register"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Register MCP Server Modal ───────────────────────────────────────────────

function RegisterMcpModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [allowlist, setAllowlist] = useState("");
  const [blocklist, setBlocklist] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name || !url) return;
    setSubmitting(true);
    setError(null);
    try {
      const allowArr = allowlist.split(",").map((s) => s.trim()).filter(Boolean);
      const blockArr = blocklist.split(",").map((s) => s.trim()).filter(Boolean);
      await api.createMcpServer({
        name,
        description: description || undefined,
        url,
        allowlist: allowArr.length > 0 ? allowArr : undefined,
        blocklist: blockArr.length > 0 ? blockArr : undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to register MCP server");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-ink-900">Add MCP Server</h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-600">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My MCP Server"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">Server URL</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://mcp-server.example.com"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">
              Tool Allowlist (comma-separated, leave empty for all)
            </label>
            <input
              value={allowlist}
              onChange={(e) => setAllowlist(e.target.value)}
              placeholder="tool_a, tool_b"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600">
              Tool Blocklist (comma-separated)
            </label>
            <input
              value={blocklist}
              onChange={(e) => setBlocklist(e.target.value)}
              placeholder="dangerous_tool"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-ink-400 focus:outline-none"
            />
          </div>
          {error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm text-ink-600 hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name || !url || submitting}
            className="rounded-lg bg-ink-950 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800 disabled:opacity-50"
          >
            {submitting ? "Adding..." : "Add Server"}
          </button>
        </div>
      </div>
    </div>
  );
}
